import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Row, Col, Button, Card, List, Progress, Typography, Space, Switch, Grid, Divider, Avatar, Dropdown, Menu, Select, Spin, Modal } from 'antd'
import { CheckOutlined, UserOutlined, StepBackwardOutlined, PlayCircleOutlined, StepForwardOutlined, CaretRightOutlined, LoadingOutlined, MailOutlined, FacebookOutlined, InstagramOutlined } from '@ant-design/icons'
import { getStorage, ref, getDownloadURL } from 'firebase/storage'
import ReactCountryFlag from 'react-country-flag'
import {
  MediaController,
  MediaControlBar,
  MediaTimeRange,
  MediaTimeDisplay,
  MediaVolumeRange,
  MediaPlayButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaMuteButton,
  MediaFullscreenButton,
  MediaPlaybackRateButton,
  MediaLoadingIndicator,
} from 'media-chrome/react'
import homeBack from '../assets/home_back.jpg'
import colozooLogoFav from '../assets/colozoo-logo-fav.png'
import PageTitle from './PageTitle'
import PageControls from './PageControls'
import { useTranslation } from 'react-i18next'
import { Course } from '../types/course'
import UserProfile from './UserProfile'
import CommentSection from './CommentSection'
import { useAuth } from '../contexts/AuthContext'
import { updateMetaTags } from '../utils/metaTagManager'

const { Title, Text } = Typography
const { useBreakpoint } = Grid

const accentColor2 = '#231F20'
const accentColor = '#279063'

// Store links for footer
const storeLinks = {
  de: {
    url: 'https://www.amazon.de/stores/Colozoo/page/088F6D23-55D5-4281-ACB4-0E0BFBDD99A3',
  },
  fr: {
    url: 'https://www.amazon.fr/stores/Colozoo/page/315C207E-1141-4BBF-83D1-F857DA73F838',
  },
  it: {
    url: 'https://www.amazon.it/stores/Colozoo/page/6FBDACCD-D6A8-4017-971A-15D2488DE0D2',
  },
  es: {
    url: 'https://www.amazon.es/stores/Colozoo/page/938FD6BE-3585-4F1E-A08F-47E8AAEC8615',
  },
  en: {
    url: 'https://www.amazon.co.uk/stores/Colozoo/page/D00BCC46-3519-4856-B012-FD748CFEE7D6',
  },
  'en-US': {
    url: 'https://www.amazon.com/stores/Colozoo/page/C207523A-55EB-4191-B263-65E44BA822FD',
  },
  pl: {
    url: 'https://www.amazon.pl/stores/Colozoo/page/1D334B31-7C3B-4859-B475-2EAE9ED518C4',
  },
  se: {
    url: 'https://www.amazon.se/stores/Colozoo/page/7DB11D18-5CEF-4567-A485-44692B2747AA',
  },
  nl: {
    url: 'https://www.amazon.nl/stores/Colozoo/page/B69123A5-4AFA-4121-8636-A07CD67B15FA',
  },
  be: {
    url: 'https://www.amazon.com.be/stores/page/0AF9CE3E-C9F7-452B-9632-B92B930C0769',
  },
}

// Map language codes to country codes
const countryCodeMap: { [key: string]: string } = {
  de: 'DE',
  fr: 'FR',
  it: 'IT',
  es: 'ES',
  en: 'GB',
  'en-US': 'US',
  pl: 'PL',
  se: 'SE',
  nl: 'NL',
  be: 'BE',
}

interface OnlineCourseProps {
  course: Course
}

const OnlineCourse: React.FC<OnlineCourseProps> = ({ course }) => {
  // ============================================================================
  // CONSTANTS AND CONFIG
  // ============================================================================

  // Storage keys for localStorage (keeping for backwards compatibility during transition)
  const STORAGE_KEY_CHAPTER = `${course.storagePrefix}_current_chapter`
  const STORAGE_KEY_CHAPTER_POSITIONS = `${course.storagePrefix}_chapter_positions`
  const STORAGE_KEY_WATCHED_CHAPTERS = `${course.storagePrefix}_watched_chapters`
  const STORAGE_KEY_COMPLETION_MODAL_SHOWN = `${course.storagePrefix}_completion_modal_shown`

  // Options for dropdowns and selectors
  const subtitleOptions = [
    { code: 'none', label: 'No Subtitles' },
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
    { code: 'fr', label: 'Français' },
    { code: 'it', label: 'Italiano' },
    { code: 'es', label: 'Español' },
  ]

  const videoLanguageOptions = [
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
    { code: 'fr', label: 'Français' },
    { code: 'it', label: 'Italiano' },
    { code: 'es', label: 'Español' },
  ]

  const qualityOptions = [
    { value: '1080p', label: '1080p' },
    { value: '720p', label: '720p' },
    { value: '480p', label: '480p' },
  ]

  // ============================================================================
  // AUTH & I18N & HOOKS
  // ============================================================================

  const { user, saveCourseProgress, loadCourseProgress } = useAuth()
  const { t, i18n } = useTranslation('onlineCourse')
  const storage = getStorage()
  const { lg } = useBreakpoint()
  const isMobile = !lg

  // ============================================================================
  // STATES
  // ============================================================================

  // Video playback state
  const [currentChapterIndex, setCurrentChapterIndex] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_CHAPTER)
    return saved ? parseInt(saved, 10) : 0
  })
  const [currentPosition, setCurrentPosition] = useState(0)
  const [targetPosition, setTargetPosition] = useState(() => {
    const savedPositions = localStorage.getItem(STORAGE_KEY_CHAPTER_POSITIONS)
    const savedChapter = localStorage.getItem(STORAGE_KEY_CHAPTER)
    if (savedPositions && savedChapter) {
      try {
        const positions = JSON.parse(savedPositions)
        const chapter = parseInt(savedChapter, 10)
        return positions[chapter] || 0
      } catch {
        return 0
      }
    }
    return 0
  })
  const [chapterPositions, setChapterPositions] = useState<{ [key: number]: number }>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_CHAPTER_POSITIONS)
    return saved ? JSON.parse(saved) : {}
  })
  const [isPlaying, setIsPlaying] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [hasLoadedFromStorage, setHasLoadedFromStorage] = useState(false)
  const [hasSeekedToSavedPosition, setHasSeekedToSavedPosition] = useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [volume, setVolume] = useState(0.8)
  const [watchedChapters, setWatchedChapters] = useState<{ [key: number]: boolean }>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_WATCHED_CHAPTERS)
    return saved ? JSON.parse(saved) : {}
  })

  // Modal state
  const [completionModalShown, setCompletionModalShown] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_COMPLETION_MODAL_SHOWN)
    return saved ? JSON.parse(saved) : false
  })
  const [completionModalVisible, setCompletionModalVisible] = useState(false)
  const [thirdChapterModalVisible, setThirdChapterModalVisible] = useState(false)
  const [thirdChapterModalShown, setThirdChapterModalShown] = useState(() => {
    const saved = localStorage.getItem(`${course.storagePrefix}_third_chapter_modal_shown`)
    return saved ? JSON.parse(saved) : false
  })

  // UI state
  const [isTabFocused, setIsTabFocused] = useState(true)
  const [isLoadingProgress, setIsLoadingProgress] = useState(false)
  const [userProfileVisible, setUserProfileVisible] = useState(false)

  // Video controls state
  const [videoLanguage, setVideoLanguage] = useState(() => {
    const saved = localStorage.getItem('video_language')
    if (saved) return saved
    // Fallback to current i18n language, normalizing GB/US to 'en'
    const currentLang = i18n.resolvedLanguage || 'en-GB'
    if (currentLang.startsWith('en-')) {
      return 'en'
    }
    return currentLang
  })
  const [videoQuality, setVideoQuality] = useState('1080p')
  const [videoUrls, setVideoUrls] = useState<{ [key: string]: string }>({})
  const [loadingVideo, setLoadingVideo] = useState(false)
  const [subtitleLang, setSubtitleLang] = useState('none')
  const [subtitleUrl, setSubtitleUrl] = useState('')

  // ============================================================================
  // REFS
  // ============================================================================

  const playerRef = useRef<HTMLVideoElement | null>(null)
  const isFetchingVideos = useRef(false)

  // ============================================================================
  // DERIVED VALUES
  // ============================================================================

  const chapters = course.chapters
  const currentChapter = chapters[currentChapterIndex]
  const watchedCount = Object.values(watchedChapters).filter(Boolean).length
  const subtitleButtonOptions = subtitleOptions.filter(opt => opt.code !== 'none')

  // ============================================================================
  // HELPER FUNCTIONS & CALLBACKS
  // ============================================================================

  // Helper function to get chapter title from course translations
  const getChapterTitle = useCallback((chapterKey: string) => {
    const key = chapterKey.replace('chapters.', '')
    const currentLang = i18n.resolvedLanguage || 'en-GB'
    const translations = course.chaptersTranslations?.[currentLang]
    if (translations && translations[key]) {
      return translations[key]
    }
    // Fallback to British English if translation not found
    return course.chaptersTranslations?.['en-GB']?.[key] || key
  }, [course.chaptersTranslations, i18n.resolvedLanguage])

  // Helper function to get course title from course translations
  const getCourseTitle = useCallback(() => {
    const currentLang = i18n.resolvedLanguage || 'en-GB'
    const title = course.titleTranslations?.[currentLang]
    if (title) {
      return title
    }
    // Fallback to British English if translation not found
    return course.titleTranslations?.['en-GB'] || course.id.charAt(0).toUpperCase() + course.id.slice(1) + ' Course'
  }, [course.titleTranslations, course.id, i18n.resolvedLanguage])

  // Set player ref using callback pattern
  const setPlayerRef = useCallback((player: HTMLVideoElement | null) => {
    if (!player) return
    playerRef.current = player
  }, [])

  // Save progress to localStorage and Firestore when it changes
  const saveToLocalStorage = useCallback(() => {
    if (hasLoadedFromStorage) {
      localStorage.setItem(STORAGE_KEY_CHAPTER, currentChapterIndex.toString())
      localStorage.setItem(STORAGE_KEY_CHAPTER_POSITIONS, JSON.stringify(chapterPositions))
      if (!user) {
        // For non-authenticated users, also save watched chapters to localStorage
        localStorage.setItem(STORAGE_KEY_WATCHED_CHAPTERS, JSON.stringify(watchedChapters))
      }
    }
  }, [hasLoadedFromStorage, currentChapterIndex, chapterPositions, watchedChapters, user])

  // Save progress to Firestore (only when not loading and user makes changes)
  const saveProgressToFirestore = useCallback(async () => {
    if (user && hasLoadedFromStorage && !isLoadingProgress && isTabFocused) {
      try {
        const progress = {
          currentChapter: currentChapterIndex,
          watchedChapters
          // Note: Modal flags (completionModalShown, secondChapterModalShown) are saved by modal effect
          // Note: chapterPositions are stored in localStorage for performance
        }
        await saveCourseProgress(course.id, progress)
      } catch (error) {
        console.error('Error saving progress to Firestore:', error)
      }
    }
  }, [
    user, 
    hasLoadedFromStorage, 
    isLoadingProgress,
    isTabFocused,
    currentChapterIndex, 
    watchedChapters,
    saveCourseProgress, 
    course.id
  ])

  // Fetch ALL quality video URLs from Firebase Storage
  const fetchAllVideoUrls = useCallback(async (chapterIndex: number, language: string) => {
    if (isFetchingVideos.current) {
      return // Prevent concurrent fetches
    }
    isFetchingVideos.current = true
    setLoadingVideo(true)
    setHasSeekedToSavedPosition(false) // Reset seek flag when loading new video
    const currentChapter = chapters[chapterIndex]
    const urls: { [key: string]: string } = {}
    
    // Fetch URLs for all quality options
    const qualities = [
      { value: '1080p', label: '1080p' },
      { value: '720p', label: '720p' },
      { value: '480p', label: '480p' },
    ]
    
    for (const quality of qualities) {
      // Normalize language code for video files (en-GB and en-US both use 'en')
      const videoLanguage = language.startsWith('en-') ? 'en' : language
      const fileName = `${currentChapter.code}.${videoLanguage}.${quality.value}.mp4`
      const path = `${course.videoBasePath}/${fileName}`
      const videoRef = ref(storage, path)
      try {
        const url = await getDownloadURL(videoRef)
        urls[quality.value] = url
      } catch (error) {
        console.error(`Error fetching video URL for ${quality.value} at path ${path}:`, error)
      }
    }
    setVideoUrls(urls)
    setLoadingVideo(false)
    isFetchingVideos.current = false
    // Force video to load new source
    if (playerRef.current) {
      const wasPlaying = !playerRef.current.paused && !playerRef.current.ended
      const currentTime = playerRef.current.currentTime
      playerRef.current.load()
      playerRef.current.currentTime = currentTime
      if (wasPlaying) {
        playerRef.current.play().catch(() => {
          // Ignore play errors
        })
      }
    }
  }, [storage, course, chapters])

  // ============================================================================
  // USE EFFECTS
  // ============================================================================

  // Update meta tags for social media sharing when course or language changes
  useEffect(() => {
    const courseTitle = getCourseTitle()
    const courseNameParam = course.titleTranslations?.[i18n.resolvedLanguage || 'en-GB'] || courseTitle
    
    // Get translated description from translations
    const metaDescription = t('meta.description', { 
      defaultValue: 'Learn {{courseName}} with Michelle Gibbs. Professional painting tutorials with subtitles in multiple languages. Enroll now on Colozoo!',
      courseName: courseNameParam
    })

    updateMetaTags({
      title: `Colozoo – ${courseTitle}`,
      description: metaDescription,
      url: window.location.href,
    })
  }, [course.id, i18n.language, t, getCourseTitle])

  // Reset video state when course changes
  useEffect(() => {
    // Reset all video-related state when course changes
    setVideoUrls({})
    setCurrentPosition(0)
    setTargetPosition(0)
    setChapterPositions({})
    setWatchedChapters({})
    setIsPlaying(false)
    setIsReady(false)
    setHasSeekedToSavedPosition(false)
    setLoadingVideo(true)
    
    // Load saved state from Firestore for the new course
    const loadProgressFromFirestore = async () => {
      setIsLoadingProgress(true)
      if (user) {
        try {
          const progress = await loadCourseProgress(course.id)
          if (progress) {
            setCurrentChapterIndex(progress.currentChapter || 0)
            setWatchedChapters(progress.watchedChapters || {})
            
            // Set target position for the current chapter (from localStorage, not Firestore)
            const currentChapter = progress.currentChapter || 0
            const savedPositions = localStorage.getItem(STORAGE_KEY_CHAPTER_POSITIONS)
            if (savedPositions) {
              try {
                const positions = JSON.parse(savedPositions)
                setChapterPositions(positions)
                if (positions[currentChapter] && positions[currentChapter] > 0) {
                  setTargetPosition(positions[currentChapter])
                }
              } catch (error) {
                setChapterPositions({})
              }
            }
            
            // Check for course completion modal and second chapter modal
            const watchedCount = progress.watchedChapters 
              ? Object.values(progress.watchedChapters).filter(Boolean).length 
              : 0
            setCompletionModalShown(progress.completionModalShown || false)
            setThirdChapterModalShown(progress.thirdChapterModalShown || false)
            
            if (watchedCount === chapters.length && watchedCount > 0 && !progress.completionModalShown) {
              setCompletionModalVisible(true)
              setCompletionModalShown(true)
              // Update progress to mark modal as shown
              const updatedProgress = { ...progress, completionModalShown: true }
              await saveCourseProgress(course.id, updatedProgress)
            } else if (watchedCount === 3 && !progress.thirdChapterModalShown) {
              setThirdChapterModalVisible(true)
              setThirdChapterModalShown(true)
              // Update progress to mark modal as shown
              const updatedProgress = { ...progress, thirdChapterModalShown: true }
              await saveCourseProgress(course.id, updatedProgress)
            }
          } else {
            // No progress in Firestore, load from localStorage
            const savedChapterIndex = localStorage.getItem(STORAGE_KEY_CHAPTER)
            const savedPositions = localStorage.getItem(STORAGE_KEY_CHAPTER_POSITIONS)
            const savedWatched = localStorage.getItem(STORAGE_KEY_WATCHED_CHAPTERS)

            if (savedChapterIndex !== null) {
              const chapterIndex = parseInt(savedChapterIndex, 10)
              if (chapterIndex >= 0 && chapterIndex < chapters.length) {
                setCurrentChapterIndex(chapterIndex)
              }
            }

            if (savedPositions !== null) {
              try {
                const positions = JSON.parse(savedPositions)
                setChapterPositions(positions)
                const currentChapter = savedChapterIndex ? parseInt(savedChapterIndex, 10) : 0
                if (positions[currentChapter] && positions[currentChapter] > 0) {
                  setTargetPosition(positions[currentChapter])
                }
              } catch (error) {
                setChapterPositions({})
              }
            }

            if (savedWatched) {
              try {
                setWatchedChapters(JSON.parse(savedWatched))
              } catch {
                setWatchedChapters({})
              }
            }
          }
        } catch (error) {
          console.error('Error loading progress from Firestore:', error)
          // Fallback to localStorage
          const savedChapterIndex = localStorage.getItem(STORAGE_KEY_CHAPTER)
          const savedPositions = localStorage.getItem(STORAGE_KEY_CHAPTER_POSITIONS)
          const savedWatched = localStorage.getItem(STORAGE_KEY_WATCHED_CHAPTERS)

          if (savedChapterIndex !== null) {
            const chapterIndex = parseInt(savedChapterIndex, 10)
            if (chapterIndex >= 0 && chapterIndex < chapters.length) {
              setCurrentChapterIndex(chapterIndex)
            }
          }

          if (savedPositions !== null) {
            try {
              const positions = JSON.parse(savedPositions)
              setChapterPositions(positions)
              const currentChapter = savedChapterIndex ? parseInt(savedChapterIndex, 10) : 0
              if (positions[currentChapter] && positions[currentChapter] > 0) {
                setTargetPosition(positions[currentChapter])
              }
            } catch (error) {
              setChapterPositions({})
            }
          }

          if (savedWatched) {
            try {
              setWatchedChapters(JSON.parse(savedWatched))
            } catch {
              setWatchedChapters({})
            }
          }
        }
      } else {
        // Non-authenticated user - load from localStorage
        const savedChapterIndex = localStorage.getItem(STORAGE_KEY_CHAPTER)
        const savedPositions = localStorage.getItem(STORAGE_KEY_CHAPTER_POSITIONS)
        const savedWatched = localStorage.getItem(STORAGE_KEY_WATCHED_CHAPTERS)

        if (savedChapterIndex !== null) {
          const chapterIndex = parseInt(savedChapterIndex, 10)
          if (chapterIndex >= 0 && chapterIndex < chapters.length) {
            setCurrentChapterIndex(chapterIndex)
          }
        }

        if (savedPositions !== null) {
          try {
            const positions = JSON.parse(savedPositions)
            setChapterPositions(positions)
            const currentChapter = savedChapterIndex ? parseInt(savedChapterIndex, 10) : 0
            if (positions[currentChapter] && positions[currentChapter] > 0) {
              setTargetPosition(positions[currentChapter])
            }
          } catch (error) {
            setChapterPositions({})
          }
        }

        if (savedWatched) {
          try {
            setWatchedChapters(JSON.parse(savedWatched))
          } catch {
            setWatchedChapters({})
          }
        }
      }
      
      setHasLoadedFromStorage(true)
      setIsLoadingProgress(false)
    }
    
    loadProgressFromFirestore()
    
    // Load global settings (not course-specific)
    const savedRate = localStorage.getItem('watercolours_playback_rate')
    const savedVolume = localStorage.getItem('watercolours_volume')
    if (savedRate) setPlaybackRate(Number(savedRate))
    if (savedVolume) setVolume(Number(savedVolume))
    
  }, [course.id, user, loadCourseProgress]) // Only trigger when course changes

  // Handle video language changes - clear video URLs but don't reset progress
  useEffect(() => {
    // Only fetch if we have loaded from storage
    if (hasLoadedFromStorage) {
      setLoadingVideo(true)
      setHasSeekedToSavedPosition(false)
      // Set target position to the saved position for this chapter
      const savedPosition = chapterPositions[currentChapterIndex] || 0
      setTargetPosition(savedPosition)
      // Fetch videos immediately for the new language
      fetchAllVideoUrls(currentChapterIndex, videoLanguage)
    }
    // Save video language to localStorage whenever it changes
    localStorage.setItem('video_language', videoLanguage)
  }, [videoLanguage, hasLoadedFromStorage, currentChapterIndex, chapterPositions, fetchAllVideoUrls])

  // Track tab focus to prevent conflicts between multiple tabs
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabFocused(!document.hidden)
    }

    const handleFocus = () => {
      setIsTabFocused(true)
    }

    const handleBlur = () => {
      setIsTabFocused(false)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
    }
  }, [])

  // Check and display modals when watched chapters change
  useEffect(() => {
    if (hasLoadedFromStorage && !isLoadingProgress && user) {
      const currentWatchedCount = Object.values(watchedChapters).filter(Boolean).length
      
      // Show third chapter modal when 3 chapters are watched
      if (
        currentWatchedCount === 3 &&
        !thirdChapterModalShown &&
        !completionModalShown &&
        !thirdChapterModalVisible
      ) {
        setThirdChapterModalVisible(true)
        setThirdChapterModalShown(true)
        // Pause video when modal opens
        if (playerRef.current) {
          playerRef.current.pause()
          setIsPlaying(false)
        }
      }
      
      // Show completion modal when all chapters are watched
      if (
        currentWatchedCount === chapters.length &&
        currentWatchedCount > 0 &&
        !completionModalShown &&
        !completionModalVisible
      ) {
        setCompletionModalVisible(true)
        setCompletionModalShown(true)
        // Pause video when modal opens
        if (playerRef.current) {
          playerRef.current.pause()
          setIsPlaying(false)
        }
      }
    }
  }, [
    hasLoadedFromStorage,
    isLoadingProgress,
    watchedChapters,
    chapters.length,
    user,
    thirdChapterModalVisible,
    completionModalVisible,
    thirdChapterModalShown,
    completionModalShown,
  ])

  // Save modal flags to Firestore when they change
  useEffect(() => {
    if (hasLoadedFromStorage && !isLoadingProgress && user && (thirdChapterModalShown || completionModalShown)) {
      const progressToSave = {
        thirdChapterModalShown,
        completionModalShown,
      }
      saveCourseProgress(course.id, progressToSave).catch(error => {
        console.error('Error saving modal flags:', error)
      })
    }
  }, [
    hasLoadedFromStorage,
    isLoadingProgress,
    user,
    thirdChapterModalShown,
    completionModalShown,
    saveCourseProgress,
    course.id,
  ])

  // Save to localStorage and Firestore when important state changes
  // Note: We intentionally exclude completionModalShown and thirdChapterModalShown from dependencies
  // because they should only be saved through explicit modal effect logic, not on every change
  useEffect(() => {
    if (!isLoadingProgress) {
      saveToLocalStorage()
      if (hasLoadedFromStorage) {
        saveProgressToFirestore()
      }
    }
  }, [
    currentChapterIndex,
    watchedChapters,
    hasLoadedFromStorage,
    isLoadingProgress,
    saveToLocalStorage,
    saveProgressToFirestore
  ])

  // Handle chapter and language changes - load position and fetch videos
  useEffect(() => {
    if (hasLoadedFromStorage) {
      const savedPosition = chapterPositions[currentChapterIndex] || 0
      setTargetPosition(savedPosition)
      setIsReady(false)
      setHasSeekedToSavedPosition(false)
      setVideoUrls({}) // Clear video URLs for new chapter/language
      setLoadingVideo(true)
      fetchAllVideoUrls(currentChapterIndex, videoLanguage)
    }
  }, [
    currentChapterIndex,
    videoLanguage,
    hasLoadedFromStorage,
    chapterPositions,
    fetchAllVideoUrls,
  ])

  // Fetch subtitle URL from Firebase Storage
  useEffect(() => {
    if (subtitleLang === 'none') {
      setSubtitleUrl('')
      return
    }
    // Use local subtitles to avoid CORS issues
    const currentChapter = chapters[currentChapterIndex]
    const localPath = `/subtitles/${course.videoBasePath}/${currentChapter.code}.${subtitleLang}.vtt`
    setSubtitleUrl(localPath)
  }, [subtitleLang, currentChapterIndex, chapters, course.videoBasePath])

  // ============================================================================
  // EVENT HANDLERS & UTILITY FUNCTIONS
  // ============================================================================

  // Mark chapter as completed when video ends
  const handleVideoEnded = () => {
    setWatchedChapters(prev => {
      if (prev[currentChapterIndex]) return prev
      return { ...prev, [currentChapterIndex]: true }
    })
  }

  // Handle chapter selection
  const handleChapterSelect = (chapterIndex: number) => {
    if (chapterIndex !== currentChapterIndex) {
      // Save current position for the current chapter before switching
      if (currentPosition > 0) {
        setChapterPositions(prev => {
          const updatedPositions = { ...prev, [currentChapterIndex]: currentPosition }
          // Save to localStorage for performance
          localStorage.setItem(STORAGE_KEY_CHAPTER_POSITIONS, JSON.stringify(updatedPositions))
          return updatedPositions
        })
      }
      
      // Switch to new chapter and load its saved position
      setCurrentChapterIndex(chapterIndex)
      const savedPosition = chapterPositions[chapterIndex] || 0
      setTargetPosition(savedPosition)
      setIsReady(false)
      setHasSeekedToSavedPosition(false)
      setVideoUrls({}) // Clear video URLs immediately
      setLoadingVideo(true)
      // Fetch videos will be handled by the useEffect when currentChapterIndex changes
    }
  }

  // Handle when player is ready - seek to saved position
  const handlePlayerReady = () => {
    if (!isReady) {
      setIsReady(true)
      
      // If we have a target position and haven't seeked yet, seek to it
      if (targetPosition > 0 && !hasSeekedToSavedPosition && !isPlaying && playerRef.current) {
        playerRef.current.currentTime = targetPosition
        setCurrentPosition(targetPosition) // Update current position to match
        setHasSeekedToSavedPosition(true)
      }
    }
  }

  // Mark chapter as watched/unwatched (toggle)
  const handleCompleteChapter = () => {
    setWatchedChapters(prev => {
      const updated = { ...prev, [currentChapterIndex]: !prev[currentChapterIndex] }
      return updated
    })
  }

  // Navigate to previous chapter
  const handlePreviousChapter = () => {
    if (currentChapterIndex > 0) {
      handleChapterSelect(currentChapterIndex - 1)
    }
  }

  // Navigate to next chapter
  const handleNextChapter = () => {
    if (currentChapterIndex < chapters.length - 1) {
      handleChapterSelect(currentChapterIndex + 1)
    }
  }

  // Handle language change from dropdown
  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language)
    setVideoLanguage(language)
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <>
      <PageTitle title={String(t('meta.title', { courseName: getCourseTitle() }))} />
      <Row
        style={{
          minHeight: '100vh',
          backgroundColor: '#66b9bf',
          position: 'relative',
          backgroundImage: `url(${homeBack})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: isMobile ? '140% auto' : '100% auto',
          backgroundPosition: isMobile ? '50% 0' : 'center 0',
          padding: 0,
        }}
        justify="center"
      >
        <Col xs={24} md={20} lg={18} style={{ zIndex: 2, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          {/* Header Section */}
          {isMobile ? (
            <>
              <Row 
                align="middle" 
                justify="center" 
                style={{ 
                  width: '100%',
                  background: 'rgba(255, 255, 255, 0.89)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  padding: '18px 32px',
                  margin: 0,
                  borderRadius: '0px',
                }}
              >
                {/* Logo and Title Section - Centered */}
                <Col style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Button
                    type="text"
                    style={{
                      padding: 0,
                      height: 'auto',
                      lineHeight: 0,
                      background: 'none',
                      border: 'none',
                      boxShadow: 'none',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={() => window.location.href = '/'}
                    aria-label="Go to main page"
                  >
                    <img
                      src={colozooLogoFav}
                      alt="Colozoo Logo"
                      style={{
                        height: 32,
                        width: 'auto',
                        display: 'inline-block',
                        margin: 0,
                        flexShrink: 0,
                      }}
                    />
                  </Button>
                  <Title level={3} style={{
                    color: '#333',
                    margin: '0 0 0 10px',
                    fontSize: '24px',
                    fontWeight: 300,
                    textAlign: 'left',
                    lineHeight: 1.2,
                  }}>
                    {t('brandTitle')}
                  </Title>
                </Col>
              </Row>
              <Row 
                align="middle" 
                justify="center" 
                style={{ 
                  width: '100%',
                  background: 'rgba(255, 255, 255, 0.89)',
                  backdropFilter: 'blur(10px)',
                  padding: '8px 32px',
                  margin: 0,
                }}
              >
                {/* Language Selector and User Profile - Centered */}
                <Col style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <PageControls onUserProfileClick={() => setUserProfileVisible(true)} />
                </Col>
              </Row>
            </>
          ) : (
            <Row 
              align="middle" 
              justify="space-between" 
              style={{ 
                width: '100%',
                background: 'rgba(255, 255, 255, 0.89)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                padding: '26px 32px',
                margin: 0,
                borderRadius: '0px',
                borderBottom: '1px solid #4a525586',
              }}
            >
              {/* Logo and Title Section */}
              <Col style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Button
                  type="text"
                  style={{
                    padding: 0,
                    height: 'auto',
                    lineHeight: 0,
                    background: 'none',
                    border: 'none',
                    boxShadow: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={() => window.location.href = '/'}
                  aria-label="Go to main page"
                >
                  <img
                    src={colozooLogoFav}
                    alt="Colozoo Logo"
                    style={{
                      height: 40,
                      width: 'auto',
                      display: 'inline-block',
                      margin: 0,
                      flexShrink: 0,
                    }}
                  />
                </Button>
                <Title level={3} style={{
                  color: '#333',
                  margin: '0 0 0 10px',
                  fontSize: '34px',
                  fontWeight: 300,
                  textAlign: 'left',
                  lineHeight: 1.2,
                }}>
                  {t('brandTitle')}
                </Title>
              </Col>

              {/* Language Selector and User Profile */}
              <Col style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <PageControls onUserProfileClick={() => setUserProfileVisible(true)} />
              </Col>
            </Row>
          )}

          {/* Main Content Section */}
          <div
            style={{
              background: 'rgba(241, 248, 248, 0.637)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              border: 'none',
              margin: 0,
              paddingTop: isMobile ? 12 : 30,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              padding: isMobile ? 8 : 32,
              overflowX: 'hidden',
            }}
          >
            
            <Row gutter={[ 42, 42 ]} justify="center" align="top">
              {/* Chapter List Section */}
              <Col xs={{ span: 24, order: isMobile ? 2 : 1 }} lg={{ span: 8, order: 1 }}>
                <style>{`
                  .chapter-item.unselected:hover {
                    background: rgba(255, 255, 255, 0.5) !important;
                  }
                  .chapter-item.selected:hover {
                    background: #329c74 !important;
                  }
                `}</style>
                <Title level={4} style={{ marginTop: 0, marginBottom: 0, color: '#333', fontWeight: 500 }}>{getCourseTitle()}</Title>
                <Text style={{ fontSize: 16, fontWeight: 600, color: '#666', marginTop: -10, marginBottom: 5 }}>
                  {t('by')} <a href="https://michellegibbs.co.uk/" target="_blank" rel="noopener noreferrer">Michelle Gibbs</a>
                </Text>
                <Space direction="vertical" size={8} style={{ marginTop: 24, marginBottom: 30, width: '100%' }}>
                  {/* <Text type="secondary" style={{ color: '#333' }}>{t('courseProgress')}</Text> */}
                  <Progress
                    percent={chapters.length === 0 ? 0 : Math.round((watchedCount / chapters.length) * 100)}
                    // showInfo={false}
                    strokeColor={accentColor}
                    size={['100%', 26]}
                    percentPosition={{ align: 'center', type: 'inner' }}
                    // style={{ marginTop: 1 }}
                  />
                  <Text style={{ fontSize: '0.8rem', color: accentColor2, fontWeight: 700 }}>
                    {t('chaptersCompleted', { count: watchedCount, total: chapters.length })}
                  </Text>
                </Space>
                <List
                  itemLayout="vertical"
                  dataSource={chapters}
                  renderItem={(chapter, index) => (
                    <List.Item
                      key={index}
                      className={`chapter-item ${index === currentChapterIndex ? 'selected' : 'unselected'}`}
                      style={{
                        background: index === currentChapterIndex ? accentColor : 'rgba(255, 255, 255, 0.315)',
                        color: index === currentChapterIndex ? '#fff' : '#333',
                        cursor: 'pointer',
                        padding: '10px 16px',
                        borderRadius: 8,
                        marginBottom: 8,
                        transition: 'background 0.2s, color 0.2s',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                      onClick={() => handleChapterSelect(index)}
                    >
                      <Space direction="vertical" size={2} style={{ flex: 1 }}>
                        <Text strong style={{ fontSize: '1.1rem', color: index === currentChapterIndex ? '#fff' : '#333' }}>
                          {index + 1}. {getChapterTitle(chapter.titleKey.replace('chapters.', ''))}
                        </Text>
                        {chapter.duration && (
                          <Text style={{ fontSize: '0.9rem', marginTop: 1, opacity: index === currentChapterIndex ? 0.8 : 0.6, color: index === currentChapterIndex ? '#fff' : '#555' }}>
                            {chapter.duration}
                          </Text>
                        )}
                      </Space>
                      {watchedChapters[index] && (
                        <CheckOutlined style={{ color: index === currentChapterIndex ? '#fff' : accentColor, fontSize: '1.3em' }} />
                      )}
                    </List.Item>
                  )}
                />
              </Col>
              {/* Video Player Section */}
              <Col xs={{ span: 24, order: 1 }} lg={{ span: 16, order: 2 }}>
                {/* Chapter Title, Duration, and Complete Checkbox - moved above video */}
                <Row align="top" justify="space-between" style={{ marginBottom: 20 }}>
                  <Col>
                    <Title level={3} style={{ margin: 0, color: '#333', fontWeight: 800 }}>
                      {getChapterTitle(currentChapter.titleKey.replace('chapters.', ''))}
                    </Title>
                    {currentChapter.duration && (
                      <Text type="secondary" style={{ color: '#555' }}>{t('duration', { duration: currentChapter.duration })}</Text>
                    )}
                  </Col>
                  <Col style={{ alignSelf: 'flex-start', marginTop:10 }}>
                    <Switch
                      checked={watchedChapters[currentChapterIndex]}
                      onChange={handleCompleteChapter}
                      checkedChildren={
                        <span>
                          <CheckOutlined style={{ color: '#fff' }} /> {t('complete')}
                        </span>
                      }
                      unCheckedChildren={<span>{t('incomplete')}</span>}
                      style={{
                        backgroundColor: watchedChapters[currentChapterIndex] ? accentColor : undefined,
                      }}
                    />
                  </Col>
                </Row>
                <div style={{ position: 'relative', width: '100%' }}>
                  <MediaController style={{ width: '100%', aspectRatio: '16/9', marginBottom: 16, borderRadius: '18px', overflow: 'hidden', position: 'relative' }}>
                    {/* Native video element with multiple sources */}
                    <video
                      slot="media"
                      ref={setPlayerRef}
                      width="100%"
                      height="100%"
                      style={{ width: '100%', height: '100%' }}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => { setIsPlaying(false); setTargetPosition(currentPosition) }}
                      onTimeUpdate={(e) => setCurrentPosition(e.currentTarget.currentTime)}
                      onEnded={handleVideoEnded}
                      onLoadedMetadata={handlePlayerReady}
                      key={`${course.id}-${currentChapterIndex}-${videoLanguage}`}
                      preload="metadata"
                    >
                      {/* Only render the selected quality source */}
                      {videoUrls[videoQuality] && (
                        <source
                          key={videoUrls[videoQuality]}
                          src={videoUrls[videoQuality]}
                          type="video/mp4"
                        />
                      )}
                      {subtitleUrl && (
                        <track
                          key={subtitleLang}
                          label={subtitleOptions.find(opt => opt.code === subtitleLang)?.label || subtitleLang}
                          kind="subtitles"
                          srcLang={subtitleLang}
                          src={subtitleUrl}
                          default
                        />
                      )}
                    </video>
                    <MediaLoadingIndicator slot="centered-chrome" />
                    {!loadingVideo && videoUrls[videoQuality] && (
                      <MediaControlBar>
                        <MediaPlayButton />
                        {!isMobile && <MediaSeekBackwardButton seekOffset={5} />}
                        {!isMobile && <MediaSeekForwardButton seekOffset={5} />}
                        <MediaTimeRange />
                        {!isMobile && <MediaTimeDisplay showDuration />}
                        <MediaPlaybackRateButton />
                        <MediaMuteButton />
                        {!isMobile && <MediaVolumeRange />}
                        <MediaFullscreenButton />
                      </MediaControlBar>
                    )}
                    {/* Overlay with navigation buttons when video is paused/stopped */}
                    {!isPlaying && !loadingVideo && videoUrls[videoQuality] && !isMobile && (
                      <div
                        style={{
                          position: 'absolute',
                          top: isMobile ? '38%' : '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: isMobile ? '200px' : '400px',
                          height: isMobile ? '120px' : '200px',
                          display: 'flex',
                          justifyContent: 'center',
                          zIndex: 15,
                          borderRadius: '18px',
                          paddingTop: isMobile ? 10 : 28,
                        }}
                      >
                        <Space direction="vertical" size={isMobile ? 'small' : 'large'} align="center">
                          <Button
                            onClick={() => playerRef.current?.play()}
                            style={{
                              minWidth: isMobile ? '80px' : '150px',
                              height: isMobile ? '80px' : '150px',
                              fontSize: isMobile ? '50px' : '100px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              background: 'rgba(0, 0, 0, 0.7)',
                              border: 'none',
                            }}
                          >
                            <CaretRightOutlined style={{ fontSize: isMobile ? '60px' : '120px', color: 'white' }} />
                          </Button>
                          <Space size={isMobile ? 'middle' : 'large'}>
                            <Button
                              type='default'
                              size={isMobile ? 'middle' : 'large'}
                              icon={<StepBackwardOutlined style={{ color:'white' }}/>}
                              onClick={handlePreviousChapter}
                              disabled={currentChapterIndex === 0}
                              style={{
                                background: 'rgba(0, 0, 0, 0.7)',
                                border: 'none',
                                color: 'white',
                                opacity: currentChapterIndex === 0 ? 0.5 : 1,
                              }}
                            >
                              {!isMobile && 'Previous Chapter'}
                            </Button>
                            <Button
                              type="default"
                              size={isMobile ? 'middle' : 'large'}
                              icon={<StepForwardOutlined style={{ color:'white' }}/>}
                              onClick={handleNextChapter}
                              disabled={currentChapterIndex === chapters.length - 1}
                              style={{
                                background: 'rgba(0, 0, 0, 0.7)',
                                border: 'none',
                                color: 'white',
                                opacity: currentChapterIndex === chapters.length - 1 ? 0.5 : 1,
                              }}
                            >
                              {!isMobile && 'Next Chapter'}
                            </Button>
                          </Space>
                        </Space>
                      </div>
                    )}
                  </MediaController>
                  {/* Loading overlay when video is loading */}
                  {loadingVideo && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'rgba(0, 0, 0, 0)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 20,
                        borderRadius: '18px',
                      }}
                    >
                      <Spin indicator={<LoadingOutlined spin />} size="large" />
                    </div>
                  )}
                  {/* Overlay for mobile tap-to-play/pause - disabled, use native video controls */}
                  {/* {isMobile && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '70%',
                        zIndex: 10,
                        background: 'transparent',
                      }}
                      onClick={() => setIsPlaying(p => !p)}
                      onTouchEnd={e => { e.preventDefault(); setIsPlaying(p => !p) }}
                    />
                  )} */}
                </div>
                {/* Subtitle Language, Video Language, and Quality Selector - moved below video */}
                <div style={{ marginTop: 12, display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label htmlFor="subtitle-lang-select" style={{ fontSize: '14px', fontWeight:600, marginBottom: 4 }}>Subtitles:</label>
                    <Select
                      id="subtitle-lang-select"
                      value={subtitleLang}
                      onChange={value => setSubtitleLang(value)}
                      style={{ width: isMobile ? 100 : 120 }}
                      size="small"
                      popupRender={menu => (
                        <div>
                          {menu}
                          <Divider style={{ margin: '8px 0' }} />
                          <Space style={{ padding: '0 8px 4px' }}>
                            <Button
                              type="text"
                              size="small"
                              onClick={() => setSubtitleLang('none')}
                              style={{ width: '100%' }}
                            >
                              {t('noSubtitles')}
                            </Button>
                          </Space>
                        </div>
                      )}
                    >
                      {subtitleOptions.map(opt => (
                        <Select.Option key={opt.code} value={opt.code}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label htmlFor="video-lang-select" style={{ fontSize: '14px', fontWeight:600, marginBottom: 4 }}>Video Language:</label>
                    <Select
                      id="video-lang-select"
                      value={videoLanguage}
                      onChange={value => setVideoLanguage(value)}
                      style={{ width: isMobile ? 100 : 120 }}
                      size="small"
                    >
                      {videoLanguageOptions.map(opt => (
                        <Select.Option key={opt.code} value={opt.code}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label htmlFor="quality-select" style={{ fontSize: '14px', fontWeight:600, marginBottom: 4 }}>Quality:</label>
                    <Select
                      id="quality-select"
                      value={videoQuality}
                      onChange={value => setVideoQuality(value)}
                      style={{ width: isMobile ? 100 : 120 }}
                      size="small"
                    >
                      {qualityOptions.map(opt => (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </div>
                {/* Comments Section - only on desktop */}
                {!isMobile && (
                  <>
                    <Divider style={{ marginTop: 34, marginBottom: 20, borderColor: '#8f8f8f' }} />
                    <CommentSection courseId={course.id} chapterId={currentChapter.code} />
                  </>
                )}
              </Col>
              {/* Comments Section - only on mobile */}
              {isMobile && (
                <Col xs={{ span: 24, order: 3 }}>
                  <Divider style={{ marginTop: 34, marginBottom: 20, borderColor: '#8f8f8f' }} />
                  <CommentSection courseId={course.id} chapterId={currentChapter.code} />
                </Col>
              )}
            </Row>

            {/* Minimalistic Footer */}
            <div style={{
              marginTop: 60,
              paddingTop: 32,
              paddingBottom: 24,
              borderTop: '1px solid #a0a0a0',
              textAlign: 'center',
            }}>
              <Row gutter={[32, 32]} justify="center">
                {/* Contacts Section */}
                <Col xs={24} sm={12} md={8}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                    <Text strong style={{ color: '#333', fontSize: 13 }}>
                      {t('contactUs')}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                    <a href="mailto:support@colozoo.de" target="_blank" rel="noopener noreferrer" title="Email">
                      <MailOutlined style={{ fontSize: 16, color: '#666' }} />
                    </a>
                    <a href="https://www.instagram.com/colozoo.official/" target="_blank" rel="noopener noreferrer" title="Instagram">
                      <InstagramOutlined style={{ fontSize: 16, color: '#666' }} />
                    </a>
                    <a href="https://www.facebook.com/Colozoo.Official/" target="_blank" rel="noopener noreferrer" title="Facebook">
                      <FacebookOutlined style={{ fontSize: 16, color: '#666' }} />
                    </a>
                  </div>
                </Col>
                
                {/* Stores Section */}
                <Col xs={24} sm={12} md={8}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                    <Text strong style={{ color: '#333', fontSize: 13 }}>
                      {t('buyOnline')}
                    </Text>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(5, 1fr)',
                      gap: 8,
                      justifyItems: 'center',
                      alignItems: 'center',
                      maxWidth: 220,
                      margin: '0 auto',
                    }}
                  >
                    {Object.keys(storeLinks).map((lng) => {
                      const link = storeLinks[lng as keyof typeof storeLinks]
                      const countryCode = countryCodeMap[lng] || 'GB'
                      return (
                        <a
                          key={lng}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={countryCode}
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <ReactCountryFlag
                            countryCode={countryCode}
                            svg
                            style={{ width: 20, height: 20 }}
                            title={countryCode}
                          />
                        </a>
                      )
                    })}
                  </div>
                </Col>

                

                {/* Legal Section */}
                <Col xs={24} sm={24} md={8}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                    <Text strong style={{ color: '#333', fontSize: 13 }}>
                      {t('legal')}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <a style={{ color: '#666', fontSize: 12 }} href='/legal-notice/' target='_blank' rel='noopener noreferrer'>
                      {t('footer_2')}
                    </a>
                    <span style={{ color: '#ccc' }}>•</span>
                    <a style={{ color: '#666', fontSize: 12 }} href='/privacy-policy/' target='_blank' rel='noopener noreferrer'>
                      {t('footer_3')}
                    </a>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>
      </Row>
      <UserProfile
        visible={userProfileVisible}
        onClose={() => setUserProfileVisible(false)}
      />
      <Modal
        title={null}
        open={completionModalVisible}
        onCancel={() => setCompletionModalVisible(false)}
        footer={null}
        centered
        width={500}
        bodyStyle={{ textAlign: 'center', padding: '40px 24px' }}
      >
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: '4rem', marginBottom: 16 }}>🎉</div>
          <Title level={2} style={{ color: '#333', marginBottom: 16 }}>
            {t('congratulations', 'Congratulations!')}
          </Title>
          <div style={{ marginBottom: 24 }}>
            <Text style={{ fontSize: '1.1rem', color: '#666', display: 'block', marginBottom: 8 }}>
              {t('courseCompleted', 'You have successfully completed the course!')}
            </Text>
            <Text style={{ fontSize: '1rem', color: '#555', display: 'block' }}>
              {t('leaveReview', 'Please take a moment to leave us a review. Your feedback helps us improve!')}
            </Text>
          </div>
          <div style={{ marginTop: 16 }}>
            <Button
              type="primary"
              href={course.reviewUrls[i18n.resolvedLanguage || 'en-GB'] || course.reviewUrls['en-GB']}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                backgroundColor: accentColor,
                borderColor: accentColor,
              }}
            >
              {t('leaveReviewButton', 'Leave a Review on Amazon')}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        title={null}
        open={thirdChapterModalVisible}
        onCancel={() => setThirdChapterModalVisible(false)}
        footer={null}
        centered
        width={500}
        bodyStyle={{ textAlign: 'center', padding: '40px 24px' }}
      >
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: '3.5rem', marginBottom: 16 }}>😊</div>
          <Title level={2} style={{ color: '#333', marginBottom: 16 }}>
            {t('greatProgress', 'Great Progress!')}
          </Title>
          <div style={{ marginBottom: 24 }}>
            <Text style={{ fontSize: '1.1rem', color: '#666', display: 'block', marginBottom: 8 }}>
              {t('happyWatching', 'We love your enthusiasm!')}
            </Text>
            <Text style={{ fontSize: '1rem', color: '#555', display: 'block' }}>
              {t('watchingProgressReview', 'Your feedback helps us improve. If you are enjoying the course, please leave a review.')}
            </Text>
          </div>
          <div style={{ marginTop: 16 }}>
            <Button
              type="primary"
              href={course.reviewUrls[i18n.resolvedLanguage || 'en-GB'] || course.reviewUrls['en-GB']}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                backgroundColor: accentColor,
                borderColor: accentColor,
              }}
            >
              {t('leaveReviewButton', 'Leave a Review on Amazon')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default OnlineCourse

